import type { TFunction } from 'i18next';

import type { IdentifierDefinitionDto } from '@/api/client';
import { ApiError } from '@/api/errors';
import { identifierDefinitionLabel } from '@/lib/identifierLabel';

/**
 * Format-validates `value` against `definition.format` (a Java regex). Returns
 * a translated, user-facing error string when the value does not match, and
 * `null` when there is no constraint to enforce or the value is acceptable.
 *
 * Mirrors the BE rule in `PersonServiceImpl.validateValueAgainstFormat`:
 * skip when `enforceFormat !== true`, when `format` is blank, or when `value`
 * is empty (presence is checked elsewhere).
 *
 * The BE uses `Pattern.matches`, which anchors implicitly. We anchor with
 * `^(?:…)$` so a JS `RegExp` test produces the same accept/reject set.
 */
export function getIdentifierFormatError(
  definition: IdentifierDefinitionDto | undefined,
  value: string,
  t: TFunction,
): string | null {
  if (definition === undefined) return null;
  if (definition.enforceFormat !== true) return null;
  const format = definition.format;
  if (format === null || format === undefined || format.length === 0) return null;
  if (value.length === 0) return null;
  let regex: RegExp;
  try {
    regex = new RegExp(`^(?:${format})$`);
  } catch {
    // Malformed regex shouldn't trip the FE — let the BE be the source of truth.
    return null;
  }
  if (regex.test(value)) return null;
  const label = identifierDefinitionLabel(definition);
  const hint = definition.formatHint;
  if (hint !== null && hint !== undefined && hint.length > 0) {
    return t('routes.patientCreate.identifiers.formatErrorWithHint', { label, hint });
  }
  // No admin-supplied hint — fall back to the raw regex so the user has some
  // signal about what's expected instead of a bare "doesn't match" message.
  return t('routes.patientCreate.identifiers.formatErrorWithPattern', {
    label,
    pattern: format,
  });
}

/**
 * Parses the BE's format-mismatch detail string and pulls out the value,
 * format and userEnteredId so the FE can substitute a friendlier message.
 *
 * BE pattern (PersonServiceImpl.validateValueAgainstFormat):
 *   `Identifier value '<value>' does not match format '<format>' for userEnteredId '<userEnteredId>'.`
 *
 * Returns `null` for unrelated errors so callers can fall back to a generic
 * "couldn't create patient" message.
 */
export function parseIdentifierFormatViolation(error: unknown): {
  readonly value: string;
  readonly format: string;
  readonly userEnteredId: string;
} | null {
  if (!(error instanceof ApiError)) return null;
  if (error.status !== 400) return null;
  const detail = error.message;
  if (typeof detail !== 'string' || detail.length === 0) return null;
  const match = detail.match(
    /Identifier value '([^']*)' does not match format '([^']*)' for userEnteredId '([^']*)'\./,
  );
  if (match === null) return null;
  const value = match[1];
  const format = match[2];
  const userEnteredId = match[3];
  if (value === undefined || format === undefined || userEnteredId === undefined) return null;
  return { value, format, userEnteredId };
}
